import { createSelector } from "reselect";
import {
  genericSelector,
  verifyIdentitySelector,
  customerInfoSelector,
  yearOptionsSelector,
  monthOptionsSelector,
  dateOptionsSelector,
  countriesSelector,
  docTypesSelector,
  showCreateOrderSectionSelector,
  showOrderInfoModalSelector,
  selfActivationSelector,
  simVerificationSelector,
  myInfoIsUsedSelector,
  formValuesSelector
} from "ducks/selectors";
import get from "lodash/get";
import includes from "lodash/includes";
import head from "lodash/head";
import split from "lodash/split";
import find from "lodash/find";
import isEmpty from "lodash/isEmpty";
import {
  constructVerifyIdentityParams,
  getChannelFromUrl,
  compactJoin
} from "utils/helpers";
import moment from "moment";
import { forms } from "utils/constants";

export const orderFromVerificationSelector = state =>
  get(state, "api.validateIccId.data", {});

export const multiSimOrderSelector = createSelector(
  orderFromVerificationSelector,
  orderDetails => get(orderDetails, "order.multi_sim_order", false)
);

// ***************************************************************
// ************** PersonalDetailsForm selectors ******************
// ***************************************************************

export const personalDetailsSelector = formValuesSelector(
  forms.PERSONAL_DETAILS_FORM
);

// ***************************************************************
// ************** PersonalDetailsForm makeSelect *****************
// ***************************************************************

export const makeSelectIsUploadIDLater = () =>
  createSelector(personalDetailsSelector, personalDetails => {
    return get(personalDetails, "upload_id_later", false);
  });

export const makeSelectPersonalDetailsForm = () => personalDetailsSelector;

export const makeSelectMultiSimOrder = () => multiSimOrderSelector;

export const makeSelectPersonalDetailsFormInitialValues = () =>
  createSelector(
    genericSelector,
    customerInfoSelector,
    yearOptionsSelector,
    monthOptionsSelector,
    dateOptionsSelector,
    countriesSelector,
    docTypesSelector,
    selfActivationSelector,
    myInfoIsUsedSelector,
    (
      generic,
      customerInfo,
      yearOptions,
      monthOptions,
      dateOptions,
      countries,
      docTypes,
      selfActivationForm,
      myInfoIsUsed
    ) => {
      const account_number = get(customerInfo, "account_number");
      const full_name =
        get(selfActivationForm, "full_name") || get(customerInfo, "name");
      const first_name = get(customerInfo, "first_name");
      const middle_name = get(customerInfo, "middle_name");
      const last_name = get(customerInfo, "last_name");
      const dobDate = head(split(get(customerInfo, "dob"), "T"));
      const dobMoment = dobDate ? moment(dobDate) : undefined;
      const dobSelfActivation = get(selfActivationForm, "dob");
      const age = dobMoment
        ? moment().diff(dobMoment, "years")
        : get(selfActivationForm, "age");
      const dob = dobMoment ? dobMoment.format("DD-MM-YYYY") : undefined;
      const dobYear = dob
        ? find(yearOptions, { value: dobMoment.year() })
        : get(selfActivationForm, "dobYear");
      const dobMonth = dob
        ? find(monthOptions, { value: dobMoment.format("MM") })
        : get(selfActivationForm, "dobMonth");
      const dobDay = dob
        ? find(dateOptions, { value: dobMoment.format("DD") })
        : get(selfActivationForm, "dobDay");
      const contact_number =
        get(selfActivationForm, "contact_number") ||
        get(customerInfo, "phone_number");
      const email =
        get(selfActivationForm, "email") || get(customerInfo, "email");

      const nationalityValue = get(customerInfo, "nationality");
      const nationality = nationalityValue
        ? find(countries, { value: nationalityValue })
        : undefined;
      const docTypeValue = get(customerInfo, "doc_type");
      const doc_type = docTypeValue
        ? find(get(docTypes, "primary"), { value: docTypeValue })
        : undefined;
      const nric = get(selfActivationForm, "nric") || get(customerInfo, "nric");
      const passexpirydate =
        get(selfActivationForm, "passexpirydate") ||
        get(customerInfo, "passexpirydate");
      const location = get(selfActivationForm, "location");
      const billing_location = get(selfActivationForm, "delivery_location");
      const billing_hse_blk_tower = get(
        selfActivationForm,
        "delivery_hse_blk_tower"
      );
      const billing_street_name = get(
        selfActivationForm,
        "delivery_street_name"
      );
      const billing_city = get(selfActivationForm, "delivery_city");
      const billing_country = get(selfActivationForm, "delivery_country");
      const billing_zip_code = get(selfActivationForm, "delivery_zip_code");
      const billing_unit_no = get(selfActivationForm, "delivery_unit_no");
      const billing_building_name = get(
        selfActivationForm,
        "delivery_building_name"
      );
      return {
        country_code: get(generic, "contact_number.prefix"),
        id_verification_required: get(
          generic,
          "id_verification_required",
          false
        ),
        delivery_location_error: "",
        billing_location_error: "",
        delivery_show_all_address_fields: false,
        billing_show_all_address_fields: false,
        same_as_delivery_address: true,
        account_number,
        full_name,
        first_name,
        middle_name,
        last_name,
        age,
        dob: dob ? dob : dobSelfActivation,
        dobYear,
        dobMonth,
        dobDay,
        contact_number,
        email,
        nationality,
        doc_type,
        nric,
        passexpirydate,
        location,
        billing_location,
        billing_hse_blk_tower,
        billing_street_name,
        billing_city,
        billing_country,
        billing_zip_code,
        billing_unit_no,
        billing_building_name,
        myInfoIsUsed
      };
    }
  );

// for self activation flow, prefill personal details
export const makeSelectDetailsFromIccidVerification = () =>
  createSelector(
    orderFromVerificationSelector,
    dateOptionsSelector,
    monthOptionsSelector,
    yearOptionsSelector,
    simVerificationSelector,
    (order, dateOptions, monthOptions, yearOptions, simVerificationForm) => {
      if (isEmpty(order)) return {};
      const dobDay = find(
        dateOptions,
        option =>
          option.value ===
          (get(order, "order.dob.date") < 10
            ? get(order, "order.dob.date")
            : get(order, "order.dob.date") ||
              get(simVerificationForm, "dobDay.value"))
      );
      const dobMonth = find(
        monthOptions,
        option =>
          option.value ===
          (get(order, "order.dob.month") < 10
            ? get(order, "order.dob.month")
            : get(order, "order.dob.month") ||
              get(simVerificationForm, "dobMonth.value"))
      );

      const dobYear = find(
        yearOptions,
        option =>
          option.value.toString() ===
          (get(order, "order.dob.year") ||
            (get(simVerificationForm, "dobYear.value") &&
              get(simVerificationForm, "dobYear.value").toString()))
      );

      const updatedDobDay = get(dobDay, "value");
      const updatedDobMonth = get(dobMonth, "value");
      const updatedDobYear = get(dobYear, "value");
      const dobString =
        updatedDobDay && updatedDobMonth && updatedDobYear
          ? `${updatedDobDay}-${updatedDobMonth}-${updatedDobYear}`
          : undefined;
      const age = dobString
        ? moment().diff(moment(dobString, "DD-MM-YYYY"), "years")
        : undefined;
      const state_of_issue = get(order, "user_details.issued_by");
      const doc_type = get(order, "user_details.doc_type");
      const [
        billing_street_name,
        billing_building_name,
        billing_village_name
      ] = split(
        get(order, "order.delivery_address.street_building_name"),
        ", "
      );
      const nric = get(simVerificationForm, "nric");
      const first_name = get(order, "user_details.first_name");
      const middle_name = get(order, "user_details.middle_name");
      const last_name = get(order, "user_details.last_name");
      const full_name = compactJoin([first_name, middle_name, last_name], " ");
      return {
        order_ref: get(order, "order.order_ref"),
        contact_number: get(order, "user_details.phone_number"),
        full_name,
        first_name,
        middle_name,
        last_name,
        email: get(order, "user_details.email"),
        nric,
        state_of_issue,
        doc_type,
        dobDay,
        dobMonth,
        dobYear,
        dob: dobString,
        age,
        billing_zip_code: get(order, "order.delivery_address.zip_code"),
        billing_hse_blk_tower: get(
          order,
          "order.delivery_address.hse_blk_tower"
        ),
        billing_street_name,
        billing_building_name,
        billing_village_name,
        billing_unit_no: get(order, "order.delivery_address.unit_no"),
        ...(get(order, "order.family_plan_sim_count") > 1 && {
          sim_count: get(order, "order.family_plan_sim_count")
        })
      };
    }
  );

export const makeSelectActivationOrderRef = () =>
  createSelector(orderFromVerificationSelector, order =>
    get(order, "order.order_ref")
  );

export const makeSelectVerifyIdentityPayload = () =>
  createSelector(
    genericSelector,
    verifyIdentitySelector,
    personalDetailsSelector,
    showCreateOrderSectionSelector,
    showOrderInfoModalSelector,
    selfActivationSelector,
    (
      generic,
      verifyIdentity,
      personalDetails,
      showOrderCreation,
      showOrderInfoModal,
      selfActivationDetails
    ) => {
      return {
        country_code: get(generic, "contact_number.prefix"),
        id_verification_required: get(
          generic,
          "id_verification_required",
          false
        ),
        skip_id_verification: includes(
          get(generic, "skip_id_verification_channels"),
          getChannelFromUrl()
        ),
        skip_verification_and_navigate: includes(
          get(generic, "skip_verification_and_navigate_channels"),
          getChannelFromUrl()
        ),
        identity_verified: get(verifyIdentity, "success", false),
        ...constructVerifyIdentityParams(personalDetails),
        showOrderCreation,
        showOrderInfoModal,
        self_activation_esim_enabled: get(
          generic,
          "self_activation_esim_enabled"
        ),
        sim_type: get(selfActivationDetails, "sim_type")
      };
    }
  );
